/**
 * IoT Device Models and Utilities
 * Data structures for managing IoT devices in InnBotics
 */

// Device Type Definitions
export const DEVICE_TYPES = {
  LIGHT: 'light',
  THERMOSTAT: 'thermostat',
  LOCK: 'lock',
  SENSOR: 'sensor',
  CAMERA: 'camera',
  SWITCH: 'switch',
  BLINDS: 'blinds',
  FAN: 'fan',
  OTHER: 'other'
};

export const DEVICE_TYPE_LABELS = {
  [DEVICE_TYPES.LIGHT]: 'Smart Light',
  [DEVICE_TYPES.THERMOSTAT]: 'Thermostat',
  [DEVICE_TYPES.LOCK]: 'Smart Lock',
  [DEVICE_TYPES.SENSOR]: 'Sensor',
  [DEVICE_TYPES.CAMERA]: 'Camera',
  [DEVICE_TYPES.SWITCH]: 'Smart Switch',
  [DEVICE_TYPES.BLINDS]: 'Smart Blinds',
  [DEVICE_TYPES.FAN]: 'Smart Fan',
  [DEVICE_TYPES.OTHER]: 'Other Device'
};

// Device Capabilities
export const CAPABILITIES = {
  SWITCH: 'switch',
  SWITCH_LEVEL: 'switchLevel',
  COLOR_CONTROL: 'colorControl',
  COLOR_TEMPERATURE: 'colorTemperature',
  THERMOSTAT: 'thermostat',
  THERMOSTAT_MODE: 'thermostatMode',
  THERMOSTAT_COOLING_SETPOINT: 'thermostatCoolingSetpoint',
  THERMOSTAT_HEATING_SETPOINT: 'thermostatHeatingSetpoint',
  LOCK: 'lock',
  MOTION_SENSOR: 'motionSensor',
  CONTACT_SENSOR: 'contactSensor',
  TEMPERATURE_MEASUREMENT: 'temperatureMeasurement',
  RELATIVE_HUMIDITY_MEASUREMENT: 'relativeHumidityMeasurement',
  VIDEO_CAMERA: 'videoCamera',
  WINDOW_SHADE: 'windowShade',
  FAN_SPEED: 'fanSpeed'
};

// Device Status
export const DEVICE_STATUS = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  UNKNOWN: 'unknown'
};

// Device Icons mapping
export const DEVICE_ICONS = {
  [DEVICE_TYPES.LIGHT]: '💡',
  [DEVICE_TYPES.THERMOSTAT]: '🌡️',
  [DEVICE_TYPES.LOCK]: '🔒',
  [DEVICE_TYPES.SENSOR]: '📡',
  [DEVICE_TYPES.CAMERA]: '📹',
  [DEVICE_TYPES.SWITCH]: '🔌',
  [DEVICE_TYPES.BLINDS]: '🪟',
  [DEVICE_TYPES.FAN]: '🌀',
  [DEVICE_TYPES.OTHER]: '⚙️'
};

/**
 * Device Model Class
 */
export class IoTDevice {
  constructor(data) {
    this.id = data.deviceId || data.id;
    this.name = data.label || data.name || 'Unnamed Device';
    this.type = data.deviceType || DEVICE_TYPES.OTHER;
    this.manufacturerName = data.manufacturerName || 'Unknown';
    this.model = data.modelName || data.model || '';
    this.locationId = data.locationId;
    this.roomId = data.roomId;
    this.capabilities = data.components?.main?.capabilities || data.capabilities || [];
    this.status = data.status || DEVICE_STATUS.UNKNOWN;
    this.state = data.state || {};
    this.metadata = data.metadata || {};
    this.createdAt = data.createdDate || new Date().toISOString();
    this.updatedAt = data.lastModifiedDate || new Date().toISOString();
  }

  // Check if device has a specific capability
  hasCapability(capability) {
    return this.capabilities.some(cap => 
      cap.id === capability || cap === capability
    );
  }

  // Get current state value for a capability
  getState(capability, attribute) {
    return this.state[capability]?.[attribute]?.value;
  }

  // Check if device is online
  isOnline() {
    return this.status === DEVICE_STATUS.ONLINE;
  }

  // Get device icon
  getIcon() {
    return DEVICE_ICONS[this.type] || DEVICE_ICONS[DEVICE_TYPES.OTHER];
  }

  // Get device type label
  getTypeLabel() {
    return DEVICE_TYPE_LABELS[this.type] || 'Unknown Device';
  }

  // Serialize to plain object
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      manufacturerName: this.manufacturerName,
      model: this.model,
      locationId: this.locationId,
      roomId: this.roomId,
      capabilities: this.capabilities,
      status: this.status,
      state: this.state,
      metadata: this.metadata,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
}

/**
 * Room Model Class
 */
export class IoTRoom {
  constructor(data) {
    this.id = data.roomId || data.id;
    this.name = data.name || 'Unnamed Room';
    this.locationId = data.locationId;
    this.devices = data.devices || [];
    this.hotelRoomNumber = data.hotelRoomNumber || null;
    this.floor = data.floor || null;
    this.metadata = data.metadata || {};
    this.createdAt = data.createdDate || new Date().toISOString();
  }

  // Add device to room
  addDevice(device) {
    if (!this.devices.find(d => d.id === device.id)) {
      this.devices.push(device);
    }
  }

  // Remove device from room
  removeDevice(deviceId) {
    this.devices = this.devices.filter(d => d.id !== deviceId);
  }

  // Get devices by type
  getDevicesByType(type) {
    return this.devices.filter(d => d.type === type);
  }

  // Count devices
  getDeviceCount() {
    return this.devices.length;
  }

  // Count online devices
  getOnlineDeviceCount() {
    return this.devices.filter(d => d.isOnline()).length;
  }

  toJSON() {
    return {
      id: this.id,
      name: this.name,
      locationId: this.locationId,
      devices: this.devices.map(d => d.toJSON()),
      hotelRoomNumber: this.hotelRoomNumber,
      floor: this.floor,
      metadata: this.metadata,
      createdAt: this.createdAt
    };
  }
}

/**
 * Validation Helpers
 */
export const validateDeviceData = (data) => {
  const errors = [];

  if (!data.name || data.name.trim().length === 0) {
    errors.push('Device name is required');
  }

  if (!data.type || !Object.values(DEVICE_TYPES).includes(data.type)) {
    errors.push('Valid device type is required');
  }

  if (!data.locationId) {
    errors.push('Location ID is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateRoomData = (data) => {
  const errors = [];

  if (!data.name || data.name.trim().length === 0) {
    errors.push('Room name is required');
  }

  if (!data.locationId) {
    errors.push('Location ID is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Utility Functions
 */

// Determine device type from capabilities
export const inferDeviceType = (capabilities) => {
  const capIds = capabilities.map(cap => cap.id || cap);

  if (capIds.includes(CAPABILITIES.LOCK)) {
    return DEVICE_TYPES.LOCK;
  }
  if (capIds.includes(CAPABILITIES.THERMOSTAT)) {
    return DEVICE_TYPES.THERMOSTAT;
  }
  if (capIds.includes(CAPABILITIES.COLOR_CONTROL) || 
      (capIds.includes(CAPABILITIES.SWITCH) && capIds.includes(CAPABILITIES.SWITCH_LEVEL))) {
    return DEVICE_TYPES.LIGHT;
  }
  if (capIds.includes(CAPABILITIES.MOTION_SENSOR) || capIds.includes(CAPABILITIES.CONTACT_SENSOR)) {
    return DEVICE_TYPES.SENSOR;
  }
  if (capIds.includes(CAPABILITIES.VIDEO_CAMERA)) {
    return DEVICE_TYPES.CAMERA;
  }
  if (capIds.includes(CAPABILITIES.WINDOW_SHADE)) {
    return DEVICE_TYPES.BLINDS;
  }
  if (capIds.includes(CAPABILITIES.FAN_SPEED)) {
    return DEVICE_TYPES.FAN;
  }
  if (capIds.includes(CAPABILITIES.SWITCH)) {
    return DEVICE_TYPES.SWITCH;
  }

  return DEVICE_TYPES.OTHER;
};

// Format device state for display
export const formatDeviceState = (device) => {
  const formatted = {};

  if (device.hasCapability(CAPABILITIES.SWITCH)) {
    formatted.power = device.getState(CAPABILITIES.SWITCH, 'switch') || 'unknown';
  }

  if (device.hasCapability(CAPABILITIES.SWITCH_LEVEL)) {
    formatted.level = device.getState(CAPABILITIES.SWITCH_LEVEL, 'level') || 0;
  }

  if (device.hasCapability(CAPABILITIES.TEMPERATURE_MEASUREMENT)) {
    formatted.temperature = device.getState(CAPABILITIES.TEMPERATURE_MEASUREMENT, 'temperature');
  }

  if (device.hasCapability(CAPABILITIES.LOCK)) {
    formatted.lockState = device.getState(CAPABILITIES.LOCK, 'lock') || 'unknown';
  }

  if (device.hasCapability(CAPABILITIES.MOTION_SENSOR)) {
    formatted.motion = device.getState(CAPABILITIES.MOTION_SENSOR, 'motion') || 'inactive';
  }

  if (device.hasCapability(CAPABILITIES.CONTACT_SENSOR)) {
    formatted.contact = device.getState(CAPABILITIES.CONTACT_SENSOR, 'contact') || 'closed';
  }

  return formatted;
};

// Group devices by room
export const groupDevicesByRoom = (devices, rooms) => {
  const grouped = {};

  rooms.forEach(room => {
    grouped[room.id] = {
      room: room,
      devices: devices.filter(d => d.roomId === room.id)
    };
  });

  // Add unassigned devices
  const unassigned = devices.filter(d => !d.roomId);
  if (unassigned.length > 0) {
    grouped['unassigned'] = {
      room: { id: 'unassigned', name: 'Unassigned Devices' },
      devices: unassigned
    };
  }

  return grouped;
};

// Get control options for device type
export const getDeviceControls = (deviceType, capabilities) => {
  const controls = [];

  switch (deviceType) {
    case DEVICE_TYPES.LIGHT:
      controls.push({ type: 'switch', label: 'On/Off' });
      if (capabilities.includes(CAPABILITIES.SWITCH_LEVEL)) {
        controls.push({ type: 'slider', label: 'Brightness', min: 0, max: 100 });
      }
      if (capabilities.includes(CAPABILITIES.COLOR_CONTROL)) {
        controls.push({ type: 'color', label: 'Color' });
      }
      break;

    case DEVICE_TYPES.THERMOSTAT:
      controls.push({ type: 'temperature', label: 'Temperature', min: 60, max: 85 });
      controls.push({ type: 'mode', label: 'Mode', options: ['auto', 'cool', 'heat', 'off'] });
      break;

    case DEVICE_TYPES.LOCK:
      controls.push({ type: 'lock', label: 'Lock/Unlock' });
      break;

    case DEVICE_TYPES.SWITCH:
      controls.push({ type: 'switch', label: 'On/Off' });
      break;

    case DEVICE_TYPES.BLINDS:
      controls.push({ type: 'slider', label: 'Position', min: 0, max: 100 });
      break;

    case DEVICE_TYPES.FAN:
      controls.push({ type: 'switch', label: 'On/Off' });
      controls.push({ type: 'slider', label: 'Speed', min: 0, max: 100 });
      break;

    default:
      break;
  }

  return controls;
};

export default {
  DEVICE_TYPES,
  DEVICE_TYPE_LABELS,
  CAPABILITIES,
  DEVICE_STATUS,
  DEVICE_ICONS,
  IoTDevice,
  IoTRoom,
  validateDeviceData,
  validateRoomData,
  inferDeviceType,
  formatDeviceState,
  groupDevicesByRoom,
  getDeviceControls
};
